import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { TimberRfqDrynessSpec, TimberSupplierStructureRef } from '../../types';
import { parseQuoteTotalInput } from '../../utils/rfqQuoteTotal';
import { parseLeadTimeWeeksRangeInput } from '../../utils/rfqLeadTimeWeeks';
import type { VendorPortalTimberSpeciesQuoteOption } from '../../utils/vendorPortalTimberQuotes';
import {
  normalizeQuotedStructures,
  resolveSubmissionQuotedStructures,
  timberQuoteStreamKeyFromParts,
} from '../../utils/timberQuoteStreamKey';
import './SupplierQuoteSubmitModal.css';

export type SupplierQuoteSubmitPayload = {
  file: File;
  quoteTotal: number;
  leadTimeWeeksMin?: number;
  leadTimeWeeksMax?: number;
  notes?: string;
  quotedSpecies?: string;
  quotedDryness?: TimberRfqDrynessSpec;
  /** Empty / omitted = full assigned scope. */
  quotedStructures?: TimberSupplierStructureRef[];
};

export type SupplierQuoteSubmitModalProps = {
  open: boolean;
  busy?: boolean;
  title?: string;
  accept?: string;
  /** Show optional lead time + notes fields (timber RFQ). */
  showDeliveryFields?: boolean;
  /** Requested timber species for this package (timber RFQ only). */
  timberSpeciesOptions?: VendorPortalTimberSpeciesQuoteOption[];
  /** True when every requested species already has a quote on this package. */
  allTimberSpeciesQuoted?: boolean;
  /** Structures the supplier may link to this quote. */
  structureOptions?: TimberSupplierStructureRef[];
  /** Published RFQ package scope (for single-structure auto-fill). */
  packageScopeStructures?: TimberSupplierStructureRef[];
  /** Existing stream keys for revision hints (timber `species|…` or CNC `cnc|…`). */
  existingTimberStreamKeys?: string[];
  /**
   * Request-round lane: the package is one structure's timber list, so the quote is
   * tagged to it automatically and the picker is hidden.
   */
  lockedStructures?: TimberSupplierStructureRef[];
  /** Optional request context shown under the title ("Joinery timber quotes · due Oct 24"). */
  requestContext?: string;
  onClose: () => void;
  onSubmit: (payload: SupplierQuoteSubmitPayload) => void | Promise<void>;
};

const SupplierQuoteSubmitModal: React.FC<SupplierQuoteSubmitModalProps> = ({
  open,
  busy = false,
  title = 'Submit quote',
  accept = '.pdf,.csv,application/pdf,text/csv',
  showDeliveryFields = false,
  timberSpeciesOptions = [],
  allTimberSpeciesQuoted = false,
  structureOptions = [],
  packageScopeStructures,
  existingTimberStreamKeys = [],
  lockedStructures,
  requestContext,
  onClose,
  onSubmit,
}) => {
  const locked = useMemo(() => normalizeQuotedStructures(lockedStructures), [lockedStructures]);
  const hasLockedStructures = locked.length > 0;
  const [quoteTotal, setQuoteTotal] = useState('');
  const [leadTimeWeeksMin, setLeadTimeWeeksMin] = useState('');
  const [leadTimeWeeksMax, setLeadTimeWeeksMax] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [species, setSpecies] = useState('');
  const [dryness, setDryness] = useState<TimberRfqDrynessSpec>('kiln_dried');
  const [selectedStructureIds, setSelectedStructureIds] = useState<string[]>([]);

  const showTimberSpeciesFields = showDeliveryFields && timberSpeciesOptions.length > 0;
  const showStructurePicker = !hasLockedStructures && structureOptions.length > 0;
  const allSpeciesQuoted = showDeliveryFields && allTimberSpeciesQuoted;

  const selectedSpeciesOption = useMemo(
    () => timberSpeciesOptions.find(option => option.species === species) ?? null,
    [timberSpeciesOptions, species],
  );

  const selectedStructures = useMemo(
    () =>
      hasLockedStructures
        ? locked
        : normalizeQuotedStructures(
            structureOptions.filter((s) => selectedStructureIds.includes(s.id)),
          ),
    [hasLockedStructures, locked, structureOptions, selectedStructureIds],
  );

  const revisesExistingStream = useMemo(() => {
    if (showTimberSpeciesFields) {
      if (!species.trim()) return false;
      const key = timberQuoteStreamKeyFromParts(
        species,
        selectedStructures.length ? selectedStructures : undefined,
      );
      return existingTimberStreamKeys.some((k) => k.trim().toLowerCase() === key);
    }
    if (!showStructurePicker && !hasLockedStructures && existingTimberStreamKeys.length === 0) return false;
    const key = timberQuoteStreamKeyFromParts(
      'cnc',
      selectedStructures.length ? selectedStructures : undefined,
    );
    return existingTimberStreamKeys.some((k) => k.trim().toLowerCase() === key);
  }, [
    showTimberSpeciesFields,
    showStructurePicker,
    hasLockedStructures,
    species,
    selectedStructures,
    existingTimberStreamKeys,
  ]);

  useEffect(() => {
    if (!open) {
      setQuoteTotal('');
      setLeadTimeWeeksMin('');
      setLeadTimeWeeksMax('');
      setNotes('');
      setFile(null);
      setSpecies('');
      setDryness('kiln_dried');
      setSelectedStructureIds([]);
      return;
    }
    const unquoted = timberSpeciesOptions.filter(option => !option.alreadyQuoted);
    const preselect =
      unquoted.length === 1 ? unquoted[0] : timberSpeciesOptions.length === 1 ? timberSpeciesOptions[0] : null;
    if (preselect) {
      setSpecies(preselect.species);
      setDryness(preselect.drynessSpec);
    }
    setSelectedStructureIds([]);
  }, [open, timberSpeciesOptions]);

  useEffect(() => {
    if (!open || structureOptions.length !== 1) return;
    setSelectedStructureIds([structureOptions[0]!.id]);
  }, [open, structureOptions]);

  if (!open) return null;

  const toggleStructure = (id: string) => {
    setSelectedStructureIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const total = parseQuoteTotalInput(quoteTotal);
    if (!total) {
      alert('Enter a valid total quote amount.');
      return;
    }
    if (!file) {
      alert('Attach a quote file (PDF or CSV).');
      return;
    }

    if (showTimberSpeciesFields && !species.trim()) {
      alert('Select the species this quote covers.');
      return;
    }

    let leadTimeWeeksMinValue: number | undefined;
    let leadTimeWeeksMaxValue: number | undefined;
    if (showDeliveryFields) {
      const leadRange = parseLeadTimeWeeksRangeInput(leadTimeWeeksMin, leadTimeWeeksMax);
      if (leadRange === null) {
        alert('Lead time must be whole weeks, with min less than or equal to max.');
        return;
      }
      if (leadRange) {
        leadTimeWeeksMinValue = leadRange.min;
        leadTimeWeeksMaxValue = leadRange.max;
      }
    }

    const resolvedStructures = hasLockedStructures
      ? locked
      : resolveSubmissionQuotedStructures({
          selected: selectedStructures,
          packageScope: packageScopeStructures,
          pickerOptions: structureOptions,
        });
    if (showStructurePicker && structureOptions.length >= 2 && !resolvedStructures?.length) {
      alert('Select at least one structure this quote applies to.');
      return;
    }

    const trimmedNotes = notes.trim();
    void onSubmit({
      file,
      quoteTotal: total,
      leadTimeWeeksMin: leadTimeWeeksMinValue,
      leadTimeWeeksMax: leadTimeWeeksMaxValue,
      notes: trimmedNotes || undefined,
      quotedSpecies: showTimberSpeciesFields ? species.trim() : undefined,
      quotedDryness: showTimberSpeciesFields ? dryness : undefined,
      quotedStructures: resolvedStructures,
    });
  };

  return (
    <div className="sqs-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sqs-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sqs-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="sqs-modal-head">
          <h2 id="sqs-modal-title">{title}</h2>
          <button type="button" className="sqs-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <form className="sqs-modal-body" onSubmit={handleSubmit}>
          {allSpeciesQuoted && (
            <p className="sqs-modal-message" role="status">
              You already have quotes for every species LCTF requested. You can still add another
              quote for a species (for example, a different structure set) or revise an existing
              one.
            </p>
          )}

          {showTimberSpeciesFields && (
            <div className="sqs-field-row">
              <label className="sqs-field">
                <span>Species</span>
                <select
                  value={species}
                  disabled={busy}
                  required
                  onChange={e => {
                    const next = e.target.value;
                    setSpecies(next);
                    const match = timberSpeciesOptions.find(option => option.species === next);
                    if (match) setDryness(match.drynessSpec);
                  }}
                >
                  <option value="">Select species…</option>
                  {timberSpeciesOptions.map(option => (
                    <option key={option.species} value={option.species}>
                      {option.species}
                      {option.alreadyQuoted ? ' (has quote)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sqs-field">
                <span>Dryness</span>
                <select
                  value={dryness}
                  disabled={busy || !species}
                  required
                  onChange={e => setDryness(e.target.value as TimberRfqDrynessSpec)}
                >
                  <option value="green">Green</option>
                  <option value="kiln_dried">Kiln-Dried</option>
                </select>
                {selectedSpeciesOption && (
                  <span className="sqs-field-hint">
                    Requested: {selectedSpeciesOption.drynessLabel}
                  </span>
                )}
              </label>
            </div>
          )}

          {hasLockedStructures && (
            <p className="sqs-field-hint sqs-locked-structures" role="status">
              This quote is for <strong>{locked.map((s) => s.name || s.id).join(' + ')}</strong>
              {requestContext ? ` — ${requestContext}` : ''}.
            </p>
          )}
          {showStructurePicker && (
            <fieldset className="sqs-field sqs-structures" disabled={busy}>
              <legend>
                Structures this quote applies to
                {structureOptions.length >= 2 ? '' : ' (optional)'}
              </legend>
              <p className="sqs-field-hint">
                {structureOptions.length >= 2
                  ? 'Select at least one structure. Upload separate PDFs when you quote different species or scopes.'
                  : 'Leave unchecked to cover your full assigned scope.'}
              </p>
              <ul className="sqs-structure-list">
                {structureOptions.map((s) => (
                  <li key={s.id}>
                    <label className="sqs-structure-option">
                      <input
                        type="checkbox"
                        checked={selectedStructureIds.includes(s.id)}
                        onChange={() => toggleStructure(s.id)}
                      />
                      <span>{s.name || s.id}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}

          {revisesExistingStream && (
            <p className="sqs-field-hint sqs-revision-hint" role="status">
              This replaces your current quote for this
              {showTimberSpeciesFields ? ' species' : ' CNC scope'}
              {selectedStructures.length
                ? ` and selected structure${selectedStructures.length === 1 ? '' : 's'}`
                : ' (full scope)'}
              . The previous file stays in quote history.
            </p>
          )}

          <label className="sqs-field">
            <span>Total quote ($)</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="e.g. 125000"
              value={quoteTotal}
              disabled={busy}
              onChange={e => setQuoteTotal(e.target.value)}
              required
            />
          </label>

          {showDeliveryFields && (
            <>
              <div className="sqs-field-row">
                <label className="sqs-field">
                  <span>Lead time min (weeks)</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="e.g. 4"
                    value={leadTimeWeeksMin}
                    disabled={busy}
                    onChange={e => setLeadTimeWeeksMin(e.target.value)}
                  />
                </label>
                <label className="sqs-field">
                  <span>Lead time max (weeks)</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="e.g. 6"
                    value={leadTimeWeeksMax}
                    disabled={busy}
                    onChange={e => setLeadTimeWeeksMax(e.target.value)}
                  />
                </label>
              </div>
              <label className="sqs-field">
                <span>Notes (optional)</span>
                <textarea
                  className="sqs-textarea"
                  rows={3}
                  placeholder="Delivery constraints, exclusions, or other context for LCTF…"
                  value={notes}
                  disabled={busy}
                  onChange={e => setNotes(e.target.value)}
                />
              </label>
            </>
          )}

          <label className="sqs-field">
            <span>Quote file</span>
            <input
              type="file"
              accept={accept}
              disabled={busy}
              required
              onChange={e => setFile(e.target.files?.[0] ?? null)}
            />
          </label>

          <div className="sqs-modal-actions">
            <button type="button" className="sqs-btn secondary" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="sqs-btn primary" disabled={busy}>
              {busy ? 'Submitting…' : revisesExistingStream ? 'Replace quote' : 'Submit quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierQuoteSubmitModal;
